import React from 'react';
import { Card, Select, Space, Button, DatePicker, Segmented } from 'antd';
import { ReloadOutlined, ClearOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useIsMobileAdmin } from '../../hooks/useMediaQuery';

const { RangePicker } = DatePicker;

export const DATE_PRESETS = [
  { key: 'today', label: 'Сегодня' },
  { key: 'yesterday', label: 'Вчера' },
  { key: '7', label: '7 дней' },
  { key: '14', label: '14 дней' },
  { key: '30', label: '30 дней' },
  { key: '90', label: '90 дней' },
  { key: 'custom', label: 'Период' },
];

export function resolvePresetRange(presetKey) {
  const now = dayjs();
  switch (presetKey) {
    case 'today':
      return [now.startOf('day'), now];
    case 'yesterday':
      return [now.subtract(1, 'day').startOf('day'), now.subtract(1, 'day').endOf('day')];
    case '7':
      return [now.subtract(7, 'day').startOf('day'), now];
    case '14':
      return [now.subtract(14, 'day').startOf('day'), now];
    case '30':
      return [now.subtract(30, 'day').startOf('day'), now];
    case '90':
      return [now.subtract(90, 'day').startOf('day'), now];
    default:
      return [now.subtract(30, 'day').startOf('day'), now];
  }
}

export function filtersToParams(filters) {
  const [from, to] = filters.dateRange || resolvePresetRange(filters.preset || '30');
  return {
    dateFrom: from?.toISOString?.() ?? from,
    dateTo: to?.toISOString?.() ?? to,
    userId: filters.userId ?? null,
    period: filters.period ?? 'day',
    days: filters.days ?? 30,
  };
}

const AdminDateFilters = ({
  filters,
  onChange,
  onApply,
  loading,
  meta,
  showUser = true,
  showPeriodGranularity = true,
  showPreset = true,
  onReset,
}) => {
  const isMobile = useIsMobileAdmin();
  const set = (patch) => onChange({ ...filters, ...patch });

  const handlePreset = (key) => {
    if (key === 'custom') {
      set({ preset: key });
      return;
    }
    set({ preset: key, dateRange: resolvePresetRange(key) });
  };

  return (
    <Card size="small" className="admin-filters-card">
      <Space wrap size="middle" align="start" className="admin-filters-card__space">
        {showPreset && (
          isMobile ? (
            <Select
              value={filters.preset || '30'}
              onChange={handlePreset}
              options={DATE_PRESETS.map((p) => ({ value: p.key, label: p.label }))}
              className="admin-filters-card__preset-select"
            />
          ) : (
            <div className="admin-filters-card__segmented">
              <Segmented
                value={filters.preset || '30'}
                onChange={handlePreset}
                options={DATE_PRESETS.map((p) => ({ value: p.key, label: p.label }))}
              />
            </div>
          )
        )}
        {(filters.preset === 'custom' || !showPreset) && (
          <RangePicker
            value={filters.dateRange}
            onChange={(range) => set({ dateRange: range, preset: 'custom' })}
            format="DD.MM.YYYY"
            allowClear={false}
          />
        )}
        {showPeriodGranularity && (
          <Select
            value={filters.period || 'day'}
            className="admin-filters-card__period-select"
            onChange={(v) => set({ period: v })}
            options={[
              { value: 'hour', label: 'По часам' },
              { value: 'day', label: 'По дням' },
              { value: 'month', label: 'По месяцам' },
              { value: 'year', label: 'По годам' },
            ]}
          />
        )}
        {showUser && (
          <Select
            allowClear
            showSearch
            placeholder="Все пользователи"
            className="admin-filters-card__user-select"
            value={filters.userId}
            onChange={(v) => set({ userId: v ?? null })}
            optionFilterProp="label"
            options={(meta?.users || []).map((u) => ({
              value: u.id,
              label: `${u.username} (#${u.id})`,
            }))}
          />
        )}
        <Button type="primary" icon={<ReloadOutlined />} onClick={onApply} loading={loading} block={isMobile}>
          Применить
        </Button>
        {onReset && (
          <Button icon={<ClearOutlined />} onClick={onReset} block={isMobile}>
            Сбросить
          </Button>
        )}
      </Space>
    </Card>
  );
};

export default AdminDateFilters;
